/**
 * sn-kaufboxen — Paketwahl (1 / 3 / 5 Dosen bzw. 1 / 2 / 3 Sets) fuer Horizon. Markup: blocks/sn-kaufboxen.liquid
 *
 * Stand 17.10.2026 (Code-Durchsicht, Befund Abschnitt 11, Punkte E1–E3):
 * - KEIN Nachladen der Produktseite mehr. Bis 16.10. loeste jeder Paket-Klick (und die Vorauswahl nach dem Laden)
 *   Horizons Varianten-Waehler aus, der die ganze Seite (~480 KB) neu holte. Solange das lief, legte Horizons
 *   Kaufformular (product-form.js) jeden Klick auf „In den Warenkorb" nur in eine Warteschlange: Haekchen blinkte,
 *   Korb blieb leer, Einschub blieb zu (1–3 s am Handy), zweiter Tipp = doppeltes Paket.
 *   Jetzt: Variante direkt ins Kaufformular (input[name=id]), Radio im versteckten Waehler stumm mitsetzen,
 *   Kaufleiste unten selbst beschriften (Variante + Preis aus data-Attributen der Zeile).
 * - Kaufknopf-Sperre: nach dem Absenden sind Kaufknopf und Kaufleisten-Knopf gesperrt („Wird hinzugefuegt …"),
 *   bis Horizon den Korb-Aufruf beantwortet hat oder der Einschub offen ist (Notausgang nach 8 s).
 *   Die Sperre darf NICHT synchron im Submit greifen: Horizon prueft dort selbst, ob ein Knopf disabled ist, und
 *   wuerde dann gar nichts senden – deshalb setTimeout(…, 0).
 * - Sicherheitsnetz aus dem Dawn-Fehler vom 19.–21.09.2026 bleibt: input[name=id] wird unmittelbar vor dem
 *   Absenden auf die aktive Zeile gesetzt (Horizon liest die Variante beim Absenden per FormData aus diesem Feld).
 * - Mengenwaehler nur bei der Einzeldose sichtbar; beim Wechsel auf ein Paket zurueck auf 1 (sonst 2 x 3 = 6 Dosen).
 */
import { StandardEvents } from '@shopify/events';

class SnKaufboxen extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    this.rows = Array.from(this.querySelectorAll('.sn-kb__zeile'));
    this.scope = this.closest('.product-details') || this.closest('.shopify-section') || document;
    // Kaufleiste unten liegt ausserhalb von .product-details, aber im selben Abschnitt
    this.section = this.closest('.shopify-section') || document;
    this.gesperrt = false;
    this.sperrTimer = 0;
    this.preisLabels = this.lesePreisLabels();

    this.rows.forEach((row) => row.addEventListener('click', () => {
      const h = this.querySelector('.sn-kb__hinweis');
      if (h) h.hidden = true;
      this.choose(row);
    }));
    this.addEventListener('keydown', this.onKey);

    // Einzeldose auf 3 (bzw. 5) hochgezaehlt → ins passende Sparpaket wechseln, sonst fehlt der Paketrabatt
    // (Ken 28.09.2026: „1 Dose + Menge 3" war teurer als „3 Dosen").
    this.onQuantity = (event) => {
      if (event.detail?.cartLine || !(event.target instanceof Node) || !this.scope.contains(event.target)) return;
      const aktiv = this.active();
      if (!aktiv || Number(aktiv.dataset.qty || 1) !== 1) return;
      const menge = Number(event.detail?.quantity);
      const paket = this.rows.find((r) => !r.disabled && Number(r.dataset.qty) === menge && menge > 1);
      if (!paket) return;
      this.choose(paket);
      this.zeigeHinweis(menge);
    };
    document.addEventListener('quantity-selector:update', this.onQuantity);

    // Erfassungsphase, also vor Horizons eigenem Submit-Handler: Variante sichern, Doppel-Absenden abfangen.
    this.onSubmitCapture = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !this.scope.contains(form)) return;
      if (!form.querySelector('input[name="id"]')) return;
      if (this.gesperrt) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      this.writeId(this.active(), form);
      this.sperren();
    };
    document.addEventListener('submit', this.onSubmitCapture, true);

    // Die Kaufleiste unten sendet nicht selbst ab, sondern klickt den echten Knopf — trotzdem absichern.
    this.onClickCapture = (event) => {
      const t = event.target instanceof Element ? event.target : null;
      if (t && t.closest('.sticky-add-to-cart__button, .add-to-cart-button')) this.writeId(this.active());
    };
    document.addEventListener('click', this.onClickCapture, true);

    // Sperre loesen, sobald Horizon den Korb-Aufruf beantwortet hat, ein Fehler kam oder der Einschub offen ist.
    this.onCartUpdate = (event) => {
      if (!this.gesperrt) return;
      const p = event.promise;
      if (p && typeof p.then === 'function') p.then(() => this.freigebenBald(), () => this.freigeben());
      else this.freigebenBald();
    };
    this.onCartError = () => this.freigeben();
    this.onDrawerOpen = () => this.freigeben();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener(StandardEvents.cartError, this.onCartError);
    document.addEventListener('theme-drawer:open', this.onDrawerOpen);

    // Startzustand: Vorauswahl (3 Dosen bzw. 1 Set, ausser ?variant= in der Adresse) sofort ins Formular,
    // Radio stumm mitsetzen, Kaufleiste beschriften. Kein Nachladen, keine Adressaenderung.
    const start = this.active();
    this.syncQuantity(start, false);
    if (start) {
      this.writeId(start);
      this.syncPicker(start);
      this.updateStickyBar(start);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onSubmitCapture, true);
    document.removeEventListener('click', this.onClickCapture, true);
    document.removeEventListener('quantity-selector:update', this.onQuantity);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener(StandardEvents.cartError, this.onCartError);
    document.removeEventListener('theme-drawer:open', this.onDrawerOpen);
    this.freigeben();
  }

  zeigeHinweis(menge) {
    const el = this.querySelector('.sn-kb__hinweis');
    if (!el) return;
    el.textContent = (el.dataset.text || '').replace('{n}', String(menge));
    el.hidden = false;
  }

  /** @returns {HTMLButtonElement | undefined} */
  active() {
    return this.rows.find((r) => r.classList.contains('is-active')) || this.rows[0];
  }

  /** @param {HTMLButtonElement} row */
  choose(row) {
    if (!row || row.disabled || row.classList.contains('is-active')) return;
    this.rows.forEach((r) => {
      const on = r === row;
      r.classList.toggle('is-active', on);
      r.setAttribute('aria-checked', String(on));
    });
    this.syncQuantity(row, true);
    this.writeId(row);
    this.syncPicker(row);
    this.updateStickyBar(row);
    document.dispatchEvent(new CustomEvent('sn:variant', { detail: { id: Number(row.dataset.variantId) } }));
  }

  /** Pfeiltasten wie bei einer echten Radiogruppe. */
  onKey = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return;
    const i = this.rows.indexOf(/** @type {any} */ (document.activeElement));
    if (i < 0) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    const next = this.rows[(i + step + this.rows.length) % this.rows.length];
    next.focus();
    this.choose(next);
  };

  /**
   * Setzt den passenden Radioknopf in Horizons verstecktem Varianten-Waehler STUMM (ohne change-Ereignis).
   * Ein change wuerde Horizon die ganze Seite nachladen lassen – genau das vermeiden wir (Befund E1/E2).
   */
  syncPicker(row) {
    const picker = this.scope.querySelector('variant-picker');
    if (!picker) return;
    const values = [row.dataset.option1, row.dataset.option2, row.dataset.option3].filter(Boolean);
    const fieldsets = picker.querySelectorAll('fieldset');
    values.forEach((val, idx) => {
      const fs = fieldsets[idx];
      if (fs) {
        const input = Array.from(fs.querySelectorAll('input[type="radio"]')).find((el) => el.value === val);
        if (input && !input.checked) input.checked = true;
        return;
      }
      const sel = picker.querySelectorAll('select')[idx];
      if (sel && sel.value !== val) sel.value = val;
    });
  }

  /** Schreibt die Variante direkt ins Kaufformular (ohne Ereignis, damit nichts neu rendert). */
  writeId(row, form) {
    if (!row || !row.dataset.variantId) return;
    const forms = form ? [form] : this.scope.querySelectorAll('form[data-type="add-to-cart-form"], product-form-component form');
    forms.forEach((f) => {
      const input = f.querySelector('input[name="id"]');
      if (input && input.value !== row.dataset.variantId) input.value = row.dataset.variantId;
    });
  }

  /** Mengenwaehler nur bei der Einzeldose; bei Paketen zurueck auf 1. */
  syncQuantity(row, reset) {
    const single = !row || Number(row.dataset.qty || 1) === 1;
    this.classList.toggle('sn-kb--einzeln', single);
    if (single || !reset) return;
    this.scope.querySelectorAll('quantity-selector-component').forEach((q) => {
      const input = q.querySelector('input');
      if (!input || input.value === '1') return;
      if (typeof q.setValue === 'function') q.setValue('1');
      else input.value = '1';
      input.dispatchEvent(new Event('change', { bubbles: true }));
      // Kaufleiste unten zeigt sonst noch die alte Menge „(2)“ (hoert nur auf Horizons quantity-selector:update)
      q.dispatchEvent(new CustomEvent('quantity-selector:update', { bubbles: true, detail: { quantity: 1 } }));
    });
  }

  /** Beschriftungen „Angebotspreis“ / „Normaler Preis“ aus Horizons Preis-Markup uebernehmen (sprachabhaengig). */
  lesePreisLabels() {
    const bar = this.section.querySelector('sticky-add-to-cart');
    const box = bar && bar.querySelector('.sticky-add-to-cart__price');
    const sale = box && box.querySelector('.price:not(.compare-at-price) .visually-hidden');
    const reg = box && box.querySelector('.compare-at-price .visually-hidden');
    return {
      sale: sale ? sale.textContent : '',
      regular: reg ? reg.textContent : '',
    };
  }

  /** Kaufleiste unten: Variantenname und Preis der aktiven Zeile (Horizon erfuhr das bisher nur ueber das Nachladen). */
  updateStickyBar(row) {
    const bar = this.section.querySelector('sticky-add-to-cart');
    if (!bar || !row) return;
    const v = bar.querySelector('.sticky-add-to-cart__variant');
    if (v && row.dataset.title) v.textContent = row.dataset.title;
    const box = bar.querySelector('.sticky-add-to-cart__price');
    const container = box && (box.querySelector('[ref="priceContainer"]') || box.firstElementChild);
    if (container && row.dataset.price) {
      const mk = (cls, label, preis) => {
        const s = document.createElement('span');
        s.className = cls;
        if (label) {
          const l = document.createElement('span');
          l.className = 'visually-hidden';
          l.textContent = label;
          s.appendChild(l);
        }
        const b = document.createElement('bdi');
        b.textContent = preis;
        s.appendChild(b);
        return s;
      };
      container.replaceChildren(mk('price-item__group price', this.preisLabels.sale, row.dataset.price));
      if (row.dataset.compare) {
        container.appendChild(mk('price-item__group price-item--regular compare-at-price', this.preisLabels.regular, row.dataset.compare));
      }
    }
    if (bar instanceof HTMLElement) bar.dataset.currentVariantId = row.dataset.variantId || '';
  }

  /** Alle Kaufknoepfe im Bereich (Formular + Kaufleiste). */
  kaufknoepfe() {
    return Array.from(this.section.querySelectorAll('product-form-component button[ref="addToCartButton"], sticky-add-to-cart button[ref="addToCartButton"]'));
  }

  sperren() {
    this.gesperrt = true;
    clearTimeout(this.sperrTimer);
    // Erst NACH Horizons Submit-Handler sperren (der prueft synchron auf disabled und wuerde sonst nichts senden).
    setTimeout(() => { if (this.gesperrt) this.knopfZustand(true); }, 0);
    this.sperrTimer = setTimeout(() => this.freigeben(), 8000);
  }

  freigebenBald() {
    clearTimeout(this.sperrTimer);
    this.sperrTimer = setTimeout(() => this.freigeben(), 400);
  }

  freigeben() {
    clearTimeout(this.sperrTimer);
    if (!this.gesperrt) return;
    this.gesperrt = false;
    this.knopfZustand(false);
  }

  /** Knoepfe sperren/freigeben und den sichtbaren Text tauschen („Wird hinzugefuegt …“). */
  knopfZustand(an) {
    const text = this.dataset.wartetText || 'Wird hinzugefügt …';
    this.kaufknoepfe().forEach((b) => {
      if (an) {
        if (b.dataset.snWartet) return;
        const knoten = this.textKnoten(b);
        if (knoten) {
          b.dataset.snWartetText = knoten.textContent;
          knoten.textContent = text;
        }
        b.dataset.snWartet = '1';
        b.setAttribute('aria-busy', 'true');
        b.disabled = true;
      } else {
        if (!b.dataset.snWartet) return;
        const knoten = this.textKnoten(b);
        if (knoten && b.dataset.snWartetText !== undefined) knoten.textContent = b.dataset.snWartetText;
        delete b.dataset.snWartet;
        delete b.dataset.snWartetText;
        b.removeAttribute('aria-busy');
        b.disabled = false;
      }
    });
  }

  /** Erster sichtbarer Textknoten im Knopf (Horizon: nackter Text neben Symbol-Spans). */
  textKnoten(el) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
      const p = n.parentElement;
      if (n.textContent && n.textContent.trim() && p && !p.classList.contains('visually-hidden') && !p.closest('.add-to-cart__added, .svg-wrapper')) return n;
    }
    return null;
  }
}

if (!customElements.get('sn-kaufboxen')) customElements.define('sn-kaufboxen', SnKaufboxen);
